import type { ProblemDetail } from "./types";

export type { ProblemDetail } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | null;
  readonly errors: Record<string, string[]>;

  constructor(status: number, problem: Partial<ProblemDetail> | null | undefined) {
    super(problem?.detail ?? problem?.title ?? `Request failed with ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = problem?.detail ?? null;
    this.errors = problem?.errors ?? {};
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

type Result<T> = { data?: T; error?: unknown; response: Response };

/** Return `data` or throw an `ApiError` carrying the problem details. */
export function unwrap<T>(result: Result<T>): T {
  if (result.response.ok) return result.data as T;
  const problem = (typeof result.error === "object" ? result.error : null) as Partial<ProblemDetail> | null;
  throw new ApiError(result.response.status, problem);
}

/** Pick a user-facing message: the server's `detail` when present, else a fallback. */
export function errorMessage(e: unknown, fallback: string): string {
  if (isApiError(e) && e.detail) return e.detail;
  return fallback;
}

/** First server-side message for `field` from a 422 problem's `errors` map, if any. */
export function fieldError(e: unknown, field: string): string | null {
  return isApiError(e) ? (e.errors[field]?.[0] ?? null) : null;
}
